import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, finalize, map, mergeMap, startWith } from 'rxjs/operators';

import {
  HealthState,
  HealthSummary,
  HealthYearSummary,
  SomaticCellCountCow,
} from '../../models/health.model';
import * as HealthActions from '../actions/health.actions';

import { HealthReportService } from './../../services/health-report.service';

@Injectable()
export class HealthEffects {
  private readonly actions$ = inject(Actions);
  private readonly healthReportService = inject(HealthReportService);
  private readonly localeService = inject(LocaleService);
  private readonly store = inject<Store<HealthState>>(Store);

  protected loadSomaticCellCountAttentions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.loadSomaticCellCountAttentions),
      mergeMap(({ date }) =>
        this.healthReportService.getSomaticCellCountAttentions(date).pipe(
          map((somaticCellCountCows: SomaticCellCountCow[]) =>
            HealthActions.loadSomaticCellCountAttentionsSuccess({ somaticCellCountCows }),
          ),
          catchError((errorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  protected loadHealthSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.loadHealthSummaryRequest),
      mergeMap(({ date }) =>
        this.healthReportService.getHealthSummary(date).pipe(
          map((healthSummary: HealthSummary) =>
            HealthActions.loadHealthSummarySuccess({ healthSummary }),
          ),
          catchError((errorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  protected loadHealthYearSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.loadHealthYearSummaryRequest),
      mergeMap(({ date }) =>
        this.healthReportService.getHealthYearSummary(date).pipe(
          map((healthYearSummary: HealthYearSummary) =>
            HealthActions.loadHealthYearSummarySuccess({ healthYearSummary }),
          ),
          catchError((errorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
